import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useGoogleMapsContext, Util, MediaQuery, InfoWindow } from '@grandvisionhq/common';
import { LocationSearchMap } from '@grandvisionhq/locations';
import { useIntl, useClickAndReserveFeatureConfig } from '@grandvisionhq/state';
import { useStoreFinderContext } from '../../context/StoreFinder/context.js';
import '@swc/helpers';
import { isEqualStores } from '../../utils/store-utils.js';
import 'lodash/deburr';
import { hasStock } from '../../utils/has-stock.js';
import '../../context/StoreFinderSlots/context.js';
import { storesLocatorResultEvent } from '../../data-layer/actions.js';
import StoreFinderItemContainer from '../StoreFinderItem/StoreFinderItemContainer.js';
import CurrentLocationPin from './CurrentLocationPin.js';
import PreferredStorePin from './PreferredStorePin.js';

const currentLocationPin = CurrentLocationPin({
    theme: {
        borderColor: '#2B2E31',
        background: '#fff'
    }
});
const preferredStorePinFallback = PreferredStorePin();
const StoreFinderMapContainer = ({ stores , infoWindowContentComponent: InfoWindowContentComponent , buttonsComponent: ButtonsComponent , showAttributesAsTags  })=>{
    const { hoveredStore: hoveredStoreInList , config , isUsingUserLocation , userLocation , isUserLocationRequested , setIsUserLocationRequested , events , preferredStore: preferredStore1 , setActiveMapInfoWindow , activeMapInfoWindow ,  } = useStoreFinderContext();
    const { googleMapsSettings: { preferredStorePin  } ,  } = useGoogleMapsContext();
    const { getLabel  } = useIntl();
    const { googleMapsId  } = config;
    const [activeStore, setActiveStore] = React.useState(null);
    const clickAndReserveMode = useClickAndReserveFeatureConfig();
    const prevStores = Util.usePrevious(stores);
    const isPhone = MediaQuery.useIsPhone();
    const [hoveredStore, setHoveredStore] = React.useState(null);
    const handleOnMarkerHover = (markerId)=>{
        setHoveredStore(markerId);
    };
    const handleOnClickUseMyLocation = React.useCallback(()=>{
        if (!isUserLocationRequested) {
            setIsUserLocationRequested(true);
        }
        if (clickAndReserveMode) {
            var ref;
            events === null || events === void 0 ? void 0 : (ref = events.onClickLocationEvent) === null || ref === void 0 ? void 0 : ref.call(events, clickAndReserveMode, 'map');
        }
    }, [
        isUserLocationRequested,
        events,
        clickAndReserveMode,
        setIsUserLocationRequested
    ]);
    const handleOnMarkerClick = React.useCallback((markerId)=>{
        if (activeMapInfoWindow === markerId) {
            setActiveMapInfoWindow(null);
            setActiveStore(null);
            return;
        }
        // We need to expand the list of stores with a preferredStore if present
        const storesWithPreferredStore = preferredStore1 ? [
            ...stores,
            preferredStore1
        ] : stores;
        const store1 = storesWithPreferredStore.find((store)=>store.globalStoreId === markerId
        );
        if (store1) {
            const dataLayerEvent = storesLocatorResultEvent({
                store: store1,
                preferredStore: (preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.globalStoreId) === store1.globalStoreId,
                interaction: 'map click - marker',
                searchResults: stores.length
            });
            DataLayer.pushEvent(dataLayerEvent);
            setActiveMapInfoWindow(markerId);
            setActiveStore(store1);
        }
    }, [
        activeMapInfoWindow,
        setActiveMapInfoWindow,
        preferredStore1,
        stores
    ]);
    const closeInfoWindow = React.useCallback(()=>{
        setActiveMapInfoWindow(null);
        setActiveStore(null);
    }, [
        setActiveMapInfoWindow,
        setActiveStore
    ]);
    const InfoWindowContent = InfoWindowContentComponent !== null && InfoWindowContentComponent !== void 0 ? InfoWindowContentComponent : StoreFinderItemContainer;
    const preferredStoreMarkerConfig = React.useCallback((preferredStore, excludedFromMapBehavior = true)=>{
        const isInactive = !!(preferredStore === null || preferredStore === void 0 ? void 0 : preferredStore.stock) && !hasStock(preferredStore.stock);
        return {
            markerId: preferredStore.globalStoreId,
            title: getLabel('stores.component.storeFinderMap.map.pin.title'),
            position: {
                lat: Number(preferredStore.lat),
                lng: Number(preferredStore.lon)
            },
            pin: preferredStorePin || preferredStorePinFallback,
            inactive: isInactive,
            infoWindow: activeMapInfoWindow === preferredStore.globalStoreId && !isPhone && /*#__PURE__*/ React.createElement(InfoWindow, {
                position: {
                    lat: Number(preferredStore.lat),
                    lng: Number(preferredStore.lon)
                },
                options: {
                    pixelOffset: {
                        width: 0,
                        height: -4
                    }
                },
                onCloseClick: closeInfoWindow
            }, /*#__PURE__*/ React.createElement(InfoWindowContent, {
                className: "store-finder-map__info-window-contents",
                store: preferredStore,
                view: "map",
                buttons: ButtonsComponent ? /*#__PURE__*/ React.createElement(ButtonsComponent, {
                    store: preferredStore,
                    view: "map"
                }) : null,
                isPreferredStore: true,
                showAttributesAsTags: showAttributesAsTags
            })),
            excludedFromMapBehavior
        };
    }, [
        ButtonsComponent,
        InfoWindowContent,
        activeMapInfoWindow,
        closeInfoWindow,
        getLabel,
        isPhone,
        preferredStorePin,
        showAttributesAsTags, 
    ]);
    const storeMarkers = React.useMemo(()=>{
        return stores.map((store)=>{
            const { globalStoreId , lat , lon  } = store;
            const isInactive = !!(store === null || store === void 0 ? void 0 : store.stock) && !hasStock(store.stock);
            const storeIsPreferredStore = store.globalStoreId === (preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.globalStoreId);
            if (storeIsPreferredStore) {
                return preferredStoreMarkerConfig(store, false);
            }
            return {
                markerId: globalStoreId,
                title: getLabel('stores.component.storeFinderMap.map.pin.title'),
                position: {
                    lat: Number(lat),
                    lng: Number(lon)
                },
                inactive: isInactive,
                infoWindow: activeMapInfoWindow === globalStoreId && !isPhone && /*#__PURE__*/ React.createElement(InfoWindow, {
                    position: {
                        lat: Number(lat),
                        lng: Number(lon)
                    },
                    options: {
                        pixelOffset: {
                            width: 0,
                            height: -4
                        }
                    },
                    onCloseClick: closeInfoWindow
                }, /*#__PURE__*/ React.createElement(InfoWindowContent, {
                    className: "store-finder-map__info-window-contents",
                    store: store,
                    view: "map",
                    buttons: ButtonsComponent ? /*#__PURE__*/ React.createElement(ButtonsComponent, {
                        store: store,
                        view: "map"
                    }) : null,
                    showAttributesAsTags: showAttributesAsTags
                }))
            };
        });
    }, [
        ButtonsComponent,
        InfoWindowContent,
        activeMapInfoWindow,
        closeInfoWindow,
        getLabel,
        isPhone,
        preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.globalStoreId,
        preferredStoreMarkerConfig,
        stores,
        showAttributesAsTags, 
    ]);
    const userLocationMarker = React.useMemo(()=>{
        if (isUsingUserLocation && userLocation) {
            return [
                {
                    position: {
                        lat: userLocation.lat,
                        lng: userLocation.lng
                    },
                    pin: currentLocationPin
                }, 
            ];
        }
        return [];
    }, [
        isUsingUserLocation,
        userLocation
    ]);
    const preferredStoreMarker = React.useMemo(()=>{
        const preferredStoreFoundInList = !!stores.filter((store)=>{
            return store.globalStoreId === (preferredStore1 === null || preferredStore1 === void 0 ? void 0 : preferredStore1.globalStoreId);
        }).length;
        // Only add preferred store marker here if not present in search results
        if (preferredStore1 && !preferredStoreFoundInList) {
            return [
                preferredStoreMarkerConfig(preferredStore1)
            ];
        }
        return [];
    }, [
        preferredStore1,
        preferredStoreMarkerConfig,
        stores
    ]);
    const markers = React.useMemo(()=>{
        return [
            ...storeMarkers,
            ...userLocationMarker,
            ...preferredStoreMarker
        ];
    }, [
        preferredStoreMarker,
        storeMarkers,
        userLocationMarker
    ]);
    const center = React.useMemo(()=>{
        if (isUsingUserLocation && userLocation !== undefined) {
            return userLocation;
        }
        return undefined;
    }, [
        userLocation,
        isUsingUserLocation
    ]);
    React.useEffect(()=>{
        if (prevStores && !isEqualStores(prevStores, stores)) {
            setActiveStore(null);
            setActiveMapInfoWindow(null);
        }
    }, [
        stores,
        prevStores,
        setActiveMapInfoWindow
    ]);
    var ref1;
    return /*#__PURE__*/ React.createElement(LocationSearchMap, {
        id: googleMapsId,
        markers: markers,
        markersUpdateConfig: {
            centerToFirst: true,
            zoom: 13
        },
        center: center,
        highlightMarkerId: (ref1 = activeStore === null || activeStore === void 0 ? void 0 : activeStore.globalStoreId) !== null && ref1 !== void 0 ? ref1 : null,
        hovered: hoveredStore !== null && hoveredStore !== void 0 ? hoveredStore : hoveredStoreInList === null || hoveredStoreInList === void 0 ? void 0 : hoveredStoreInList.globalStoreId,
        userLocation: isUsingUserLocation && userLocation ? userLocation : undefined,
        userLocationZoomLevel: 13,
        centerOnUserLocation: true,
        activeLocation: isPhone && activeStore ? /*#__PURE__*/ React.createElement("div", {
            className: "store-finder-map__active-location"
        }, activeStore ? /*#__PURE__*/ React.createElement(InfoWindowContent, {
            className: "store-finder-map__active-location-contents",
            store: activeStore,
            buttons: ButtonsComponent ? /*#__PURE__*/ React.createElement(ButtonsComponent, {
                store: activeStore,
                view: "map"
            }) : null,
            view: "map",
            showAttributesAsTags: showAttributesAsTags
        }) : null) : null,
        onClickUseMyLocation: handleOnClickUseMyLocation,
        onMarkerClick: handleOnMarkerClick,
        onMarkerHover: handleOnMarkerHover,
        onMapClick: closeInfoWindow
    });
};

export { StoreFinderMapContainer as default };
//# sourceMappingURL=StoreFinderMapContainer.js.map
